import React, { useState } from 'react';
import { ClerkProvider } from '@clerk/clerk-react';
import { Routes, Route, Navigate } from 'react-router-dom';
import Calculator from './features/calculator/Calculator';
import FuturesCalculator from './features/futures/FuturesCalculator';
import ForexCalculator from './features/forex/ForexCalculator';
import StocksCalculator from './features/stocks/StocksCalculator';
import PocketCalc from './features/pocket/PocketCalc';
import SimpleSignInPage from './pages/SimpleSignInPage';
import SubscriptionRequired from './components/SubscriptionRequired';
import NavigationBar from './components/base/NavigationBar';
import AuthContextProvider, { useAuth } from './contexts/AuthContext';
import { ConfigProvider } from './contexts/ConfigContext';
import { CopyProvider } from './contexts/CopyContext';
import { dark } from '@clerk/themes';
import './App.css';

// NEW, FIXED ProtectedRoute
const ProtectedRoute = ({ children }) => {
  const { isAuthenticated, isLoading, isSubscribed } = useAuth();

  console.log('[ProtectedRoute] Auth state:', { isAuthenticated, isLoading, isSubscribed });

  if (isLoading) {
    console.log('[ProtectedRoute] Showing loading - state is loading');
    return (
      <div className="flex items-center justify-center min-h-screen bg-slate-950">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-orange-500"></div>
      </div>
    );
  }

  // If user is not authenticated, show sign-in page RIGHT HERE,
  // instead of redirecting.
  if (!isAuthenticated) {
    console.log('[ProtectedRoute] User not authenticated, showing sign-in page');
    return <SimpleSignInPage />;
  }

  // FIX: More detailed subscription check
  console.log('[ProtectedRoute] 🔍 DETAILED SUBSCRIPTION CHECK:');
  console.log('[ProtectedRoute] - isSubscribed value:', isSubscribed);
  console.log('[ProtectedRoute] - Type of isSubscribed:', typeof isSubscribed);
  console.log('[ProtectedRoute] - Boolean conversion:', Boolean(isSubscribed));

  if (!isSubscribed) {
    console.log('[ProtectedRoute] ❌ User authenticated but NOT subscribed - showing subscription page');
    return <SubscriptionRequired />;
  }

  console.log('[ProtectedRoute] ✅ User authenticated and subscribed - showing protected content');
  console.log('[ProtectedRoute] 🚨 WARNING: If user should not have access, check logic above!');
  return children;
};

// REMOVED: getEnvVariable - no longer using process.env in React
// All configurations now only through window.CLERK_CONFIG (runtime)

function App() {
  const [calculatorType, setCalculatorType] = useState('Crypto');
  const isElectron = (typeof window !== 'undefined') && window.electronAPI && window.electronAPI.isElectron;
  // App build → compact Pocket UI; website → full card UI. `?ui=pocket` forces it in the browser (preview/testing).
  const usePocket = !!isElectron || (typeof window !== 'undefined' && new URLSearchParams(window.location.search).get('ui') === 'pocket');
  
  console.log('[App] Application starting...');
  console.log('[App] Environment check - isElectron:', isElectron);
  console.log('[App] window.electronAPI available:', !!(typeof window !== 'undefined' && window.electronAPI));

  // ENVIRONMENT AGNOSTIC: Get configuration only from window.CLERK_CONFIG
  const getClerkConfig = () => {
    // Check for global configuration (SINGLE source of configuration)
    if (typeof window !== 'undefined' && window.CLERK_CONFIG) {
      console.log('[App] ✅ ENVIRONMENT AGNOSTIC: Using window.CLERK_CONFIG');

      // Add only baseTheme for compatibility with @clerk/themes
      const config = {
        ...window.CLERK_CONFIG,
        appearance: {
          ...window.CLERK_CONFIG.appearance,
          baseTheme: dark  // Add @clerk/themes base theme
        }
      };

      // Additional settings for Electron
      if (isElectron) {
        console.log("[App] Running in Electron environment");

        // Extend allowedRedirectOrigins for Electron if not set
        if (!config.allowedRedirectOrigins) {
          const baseOrigin = window.location.origin;
          config.allowedRedirectOrigins = [
            `${baseOrigin}/calculator`,
            `${baseOrigin}/calculator/`,
            "https://clerk.quer.us",
            "https://accounts.google.com"
          ];
        }
      } else {
        console.log("[App] Running in browser environment");
      }

      return config;
    }

    // CRITICAL ERROR: window.CLERK_CONFIG is missing
    console.error('[App] ❌ CRITICAL ERROR: window.CLERK_CONFIG not found!');
    console.error('[App] React Calculator requires runtime configuration for environment agnostic architecture');
    console.error('[App] Check for clerk-config.js in /public/');

    return null;
  };

  const clerkConfig = getClerkConfig();
  
  console.log('[App.jsx] 🔒 VERSION 6.1: VERSIONED CONFIGURATION - safe for existing users!', clerkConfig);
  console.log('[App.jsx] 🚀 OAuth → FULL URL signInForceRedirectUrl config JSON:', JSON.stringify(clerkConfig, null, 2));

  // 🔥 CONFIG VERSION CHECK
  console.log('[App.jsx] 🔥 CONFIG VERSION CHECK:', {
    version: clerkConfig.configVersion || 'UNKNOWN',
    timestamp: clerkConfig.configTimestamp || 'UNKNOWN',
    hasOAuthRedirects: !!clerkConfig.signInForceRedirectUrl,
    hasForceRedirect: !!clerkConfig.signInForceRedirectUrl
  });

  console.log('[App.jsx] REDIRECT DEBUGGING:', {
    hasForceSignInRedirect: !!clerkConfig.signInForceRedirectUrl,
    hasForceSignUpRedirect: !!clerkConfig.signUpForceRedirectUrl,
    signInFallback: clerkConfig.signInFallbackRedirectUrl,
    signUpFallback: clerkConfig.signUpFallbackRedirectUrl,
    allowedOrigins: clerkConfig.allowedRedirectOrigins
  });

  // ENVIRONMENT AGNOSTIC: Check for runtime configuration presence
  if (!clerkConfig || !clerkConfig.publishableKey) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-slate-950">
        <div className="text-center">
          <h1 className="text-2xl font-bold text-red-500 mb-4">Runtime Configuration Error</h1>
          <p className="text-slate-300 mb-4">
            React Calculator requires runtime configuration for environment agnostic architecture.
          </p>
          <p className="text-slate-400 text-sm">
            Check for clerk-config.js in /public/ or run deployment to generate configuration.
          </p>
        </div>
      </div>
    );
  }

  return (
    <ClerkProvider {...clerkConfig}>
      <ConfigProvider>
        <AuthContextProvider>
          <CopyProvider>
          <Routes>
            <Route 
              path="/" 
              element={
                <ProtectedRoute>
                  {usePocket ? (
                    <PocketCalc />
                  ) : (
                  <div className="min-h-screen bg-slate-950">
                    <NavigationBar
                      activeType={calculatorType}
                      onTypeChange={setCalculatorType}
                    />
                    {/* Both calculators stay mounted to preserve state on tab switch */}
                    <div className={calculatorType === 'Crypto' ? '' : 'hidden'}>
                      <Calculator />
                    </div>
                    <div className={calculatorType === 'Futures' ? '' : 'hidden'}>
                      <FuturesCalculator />
                    </div>
                    <div className={calculatorType === 'Forex' ? '' : 'hidden'}>
                      <ForexCalculator />
                    </div>
                    <div className={calculatorType === 'Stocks' ? '' : 'hidden'}>
                      <StocksCalculator />
                    </div>
                  </div>
                  )}
                </ProtectedRoute>
              } 
            />
            <Route path="*" element={<Navigate to="/" replace />} />
          </Routes>
        </CopyProvider>
      </AuthContextProvider>
      </ConfigProvider>
    </ClerkProvider>
  );
}

export default App;